import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Users, Search, ArrowLeft, MessageCircle, Award, Briefcase, 
  Globe, Target, Percent, Phone, Facebook, Instagram, Linkedin, 
  Twitter, Youtube, ExternalLink, User, Send, Sparkles, Handshake,
  Gift, Shield, Settings, AlertTriangle, FileText, CheckCircle,
  BookOpen, Lock, Info, Headphones
} from 'lucide-react';
import { toast } from 'sonner';
import MessagesPanel from '@/components/MessagesPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const NetworkingPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [total, setTotal] = useState(0);
  const [showMessages, setShowMessages] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'author', 'consumer'
  const [profileVisibility, setProfileVisibility] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [showSecurityProtocol, setShowSecurityProtocol] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportData, setReportData] = useState({
    reported_user_name: '',
    reported_service: '',
    incident_type: '',
    incident_date: '',
    description: '',
    had_payment: false,
    payment_amount: '',
    payment_method: '',
    desired_resolution: ''
  });
  const [submittingReport, setSubmittingReport] = useState(false);

  useEffect(() => {
    if (token) {
      fetchAuthors();
      fetchProfileVisibility();
    }
  }, [token, searchTerm, filterType]);

  const fetchProfileVisibility = async () => {
    try {
      const response = await axios.get(
        `${API}/api/user/profile-visibility`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfileVisibility(response.data);
    } catch (error) {
      console.error('Error fetching profile visibility:', error);
    }
  };

  const fetchAuthors = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterType !== 'all') params.append('user_type', filterType);
      params.append('limit', '50');

      const response = await axios.get(
        `${API}/api/networking/authors?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAuthors(response.data.authors);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching members:', error);
      if (error.response?.status === 403) {
        toast.error('Necesitas membresía activa para acceder al networking');
      }
    } finally {
      setLoading(false);
    }
  };

  const openAuthorProfile = async (authorId) => {
    try {
      const response = await axios.get(
        `${API}/api/networking/author/${authorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedAuthor(response.data);
    } catch (error) {
      toast.error('Error al cargar perfil');
    }
  };

  const handleEnableNetworking = async () => {
    if (!acceptedTerms) {
      toast.error('Debes aceptar los términos y condiciones');
      return;
    }
    
    setSavingVisibility(true);
    try {
      const newVisibility = {
        ...profileVisibility,
        networking_public: true,
        networking_terms_accepted: true,
        networking_terms_accepted_at: new Date().toISOString()
      };
      
      await axios.put(
        `${API}/api/user/profile-visibility`,
        { visibility: newVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProfileVisibility(newVisibility);
      setShowDisclaimer(false);
      toast.success('¡Tu perfil ahora es visible en la comunidad!');
    } catch (error) {
      toast.error('Error al activar el perfil público');
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleDisableNetworking = async () => {
    setSavingVisibility(true);
    try {
      const newVisibility = {
        ...profileVisibility,
        networking_public: false
      };
      
      await axios.put(
        `${API}/api/user/profile-visibility`,
        { visibility: newVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProfileVisibility(newVisibility);
      toast.success('Tu perfil ya no es visible en la comunidad');
    } catch (error) {
      toast.error('Error al desactivar el perfil público');
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleToggleContactField = async (field, value) => {
    try {
      const newVisibility = {
        ...profileVisibility,
        [field]: value
      };
      
      await axios.put(
        `${API}/api/user/profile-visibility`,
        { visibility: newVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProfileVisibility(newVisibility);
      toast.success(value ? `${field.replace('social_', '').replace('_', ' ')} ahora es visible` : `${field.replace('social_', '').replace('_', ' ')} oculto`);
    } catch (error) {
      toast.error('Error al actualizar visibilidad');
    }
  };

  const handleSubmitReport = async () => {
    if (!reportData.reported_user_name || !reportData.incident_type || !reportData.description) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }
    
    setSubmittingReport(true);
    try {
      await axios.post(
        `${API}/api/networking/report-incident`,
        {
          ...reportData,
          reporter_id: user?.id,
          reporter_email: user?.email,
          reporter_name: user?.full_name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Reporte enviado correctamente. Revisaremos tu caso en 48-72 horas.');
      setShowReportForm(false);
      setReportData({
        reported_user_name: '',
        reported_service: '',
        incident_type: '',
        incident_date: '',
        description: '',
        had_payment: false,
        payment_amount: '',
        payment_method: '',
        desired_resolution: ''
      });
    } catch (error) {
      toast.error('Error al enviar el reporte');
    } finally {
      setSubmittingReport(false);
    }
  };

  if (!token) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #080c2d 0%, #1a1a3e 100%)' }}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="border-purple-500/50 text-white hover:bg-purple-500/20"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div>
              <h1 className="gotham-bold text-3xl text-white flex items-center gap-3">
                <Handshake className="w-8 h-8 text-purple-400" />
                Community Networking
              </h1>
              <p className="text-white/60 verdana-regular">
                Tu Círculo de Confianza
              </p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-purple-400 gotham-bold text-2xl">{total}</span>
            <p className="text-white/60 text-sm">Miembros activos</p>
          </div>
        </div>

        {/* Welcome Message Card */}
        <Card className="card-viubux-dark border-purple-500/30 mb-6 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <CardContent className="pt-6 relative">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-6 h-6 text-yellow-400" />
                  <h2 className="gotham-bold text-xl text-white">Bienvenido a la Zona de Networking</h2>
                </div>
                <p className="text-white/70 verdana-regular text-sm leading-relaxed mb-4">
                  El espacio diseñado para que las mentes brillantes se encuentren. Aquí no solo ves nombres; 
                  descubres la <span className="text-purple-400 font-semibold">Propuesta de Valor</span> de cada miembro de nuestra comunidad.
                </p>
                <p className="text-white/70 verdana-regular text-sm leading-relaxed mb-4">
                  ¿Buscas un diseñador, un editor, un estratega o un servicio especializado? 
                  <span className="text-white font-semibold"> Búscalo aquí, conoce su oferta y contáctalo directamente.</span>
                </p>
                
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <Gift className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-green-400 gotham-bold text-sm mb-1">El Plus de la Comunidad</p>
                      <p className="text-white/70 verdana-regular text-sm">
                        Al ser parte de ViubuxConnect, fomentamos una cultura de apoyo mutuo. 
                        Si contactas a un profesional a través de este portal, pregunta por su 
                        <span className="text-green-400 font-semibold"> Beneficio Exclusivo</span>. 
                        ¡Y no olvides configurar tu perfil para ofrecer tus propios descuentos a otros miembros!
                      </p>
                    </div>
                  </div>
                </div>

                {/* Security & Report Buttons */}
                <div className="flex flex-wrap gap-3 mt-4">
                  <Button
                    onClick={() => setShowSecurityProtocol(true)}
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                    size="sm"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Protocolo de Seguridad
                  </Button>
                  <Button
                    onClick={() => setShowReportForm(true)}
                    variant="outline"
                    className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
                    size="sm"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Reportar Incidente
                  </Button>
                </div>
              </div>

              {/* Profile Visibility Toggle - Solo para Autores */}
              {user?.user_type === 'author' && (
              <div className="lg:w-80 flex-shrink-0">
                <div className="bg-black/30 border border-purple-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Settings className="w-5 h-5 text-purple-400" />
                    <h3 className="gotham-bold text-white">Tu Perfil en la Comunidad</h3>
                  </div>
                  
                  {profileVisibility?.networking_public && profileVisibility?.networking_terms_accepted ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-green-400 verdana-regular text-sm">Tu perfil es visible</span>
                      </div>
                      
                      {/* Contact Visibility Settings */}
                      <div className="space-y-3 pt-2 border-t border-purple-500/20">
                        <p className="text-white/70 text-xs gotham-medium">¿Qué información de contacto compartir?</p>
                        
                        <label className="flex items-center gap-3 cursor-pointer hover:bg-purple-500/10 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={profileVisibility?.whatsapp || false}
                            onChange={(e) => handleToggleContactField('whatsapp', e.target.checked)}
                            className="w-4 h-4 rounded bg-black/50 border-purple-500/50 text-purple-500 focus:ring-purple-500"
                          />
                          <Phone className="w-4 h-4 text-green-400" />
                          <span className="text-white text-sm">WhatsApp</span>
                          {user?.whatsapp && <span className="text-white/40 text-xs ml-auto">{user.whatsapp}</span>}
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer hover:bg-purple-500/10 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={profileVisibility?.social_instagram || false}
                            onChange={(e) => handleToggleContactField('social_instagram', e.target.checked)}
                            className="w-4 h-4 rounded bg-black/50 border-purple-500/50 text-purple-500 focus:ring-purple-500"
                          />
                          <Instagram className="w-4 h-4 text-pink-400" />
                          <span className="text-white text-sm">Instagram</span>
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer hover:bg-purple-500/10 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={profileVisibility?.social_facebook || false}
                            onChange={(e) => handleToggleContactField('social_facebook', e.target.checked)}
                            className="w-4 h-4 rounded bg-black/50 border-purple-500/50 text-purple-500 focus:ring-purple-500"
                          />
                          <Facebook className="w-4 h-4 text-blue-400" />
                          <span className="text-white text-sm">Facebook</span>
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer hover:bg-purple-500/10 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={profileVisibility?.social_linkedin || false}
                            onChange={(e) => handleToggleContactField('social_linkedin', e.target.checked)}
                            className="w-4 h-4 rounded bg-black/50 border-purple-500/50 text-purple-500 focus:ring-purple-500"
                          />
                          <Linkedin className="w-4 h-4 text-blue-500" />
                          <span className="text-white text-sm">LinkedIn</span>
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer hover:bg-purple-500/10 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={profileVisibility?.social_twitter || false}
                            onChange={(e) => handleToggleContactField('social_twitter', e.target.checked)}
                            className="w-4 h-4 rounded bg-black/50 border-purple-500/50 text-purple-500 focus:ring-purple-500"
                          />
                          <Twitter className="w-4 h-4 text-sky-400" />
                          <span className="text-white text-sm">Twitter/X</span>
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer hover:bg-purple-500/10 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={profileVisibility?.social_youtube || false}
                            onChange={(e) => handleToggleContactField('social_youtube', e.target.checked)}
                            className="w-4 h-4 rounded bg-black/50 border-purple-500/50 text-purple-500 focus:ring-purple-500"
                          />
                          <Youtube className="w-4 h-4 text-red-500" />
                          <span className="text-white text-sm">YouTube</span>
                        </label>
                        
                        <p className="text-white/40 text-xs pt-2">
                          Solo se mostrarán los campos que tengas configurados en tu perfil.
                        </p>
                      </div>
                      
                      <Button
                        onClick={handleDisableNetworking}
                        disabled={savingVisibility}
                        variant="outline"
                        className="w-full border-red-500/50 text-red-400 hover:bg-red-500/20"
                        size="sm"
                      >
                        {savingVisibility ? 'Guardando...' : 'Ocultar mi perfil'}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <span className="text-yellow-400 verdana-regular text-sm">Tu perfil está oculto</span>
                      </div>
                      <p className="text-white/50 text-xs verdana-regular">
                        Activa tu perfil para que otros miembros te encuentren.
                      </p>
                      <Button
                        onClick={() => setShowDisclaimer(true)}
                        className="w-full btn-viubux"
                        size="sm"
                      >
                        Activar mi perfil
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              )}
              
              {/* Info para Audio-Lectores */}
              {user?.user_type === 'consumer' && (
              <div className="lg:w-80 flex-shrink-0">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Info className="w-5 h-5 text-blue-400" />
                    <h3 className="gotham-bold text-white">Comunidad ViubuxConnect</h3>
                  </div>
                  <p className="text-white/70 verdana-regular text-sm mb-4">
                    Como Audio-Lector puedes explorar los perfiles de los autores y enviarles mensajes o comentarios.
                  </p>
                  <p className="text-white/50 verdana-regular text-xs">
                    La creación de perfiles públicos es un beneficio exclusivo para Autores.
                  </p>
                </div>
              </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Filters and Search */}
        <Card className="card-viubux-dark border-purple-500/30 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Type Filter */}
              <div className="flex gap-2">
                <Button
                  variant={filterType === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilterType('all')}
                  className={filterType === 'all' ? 'btn-viubux' : 'border-purple-500/50 text-white hover:bg-purple-500/20'}
                  size="sm"
                >
                  Todos
                </Button>
                <Button
                  variant={filterType === 'author' ? 'default' : 'outline'}
                  onClick={() => setFilterType('author')}
                  className={filterType === 'author' ? 'bg-green-600 hover:bg-green-700' : 'border-green-500/50 text-green-400 hover:bg-green-500/20'}
                  size="sm"
                >
                  Autores
                </Button>
                <Button
                  variant={filterType === 'consumer' ? 'default' : 'outline'}
                  onClick={() => setFilterType('consumer')}
                  className={filterType === 'consumer' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-500/50 text-blue-400 hover:bg-blue-500/20'}
                  size="sm"
                >
                  Audio-Lectores
                </Button>
              </div>
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre, profesión o bio..."
                  className="w-full bg-black/30 border border-purple-500/30 rounded-xl pl-12 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-purple-500"
                  data-testid="networking-search"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members Grid */}
        {loading ? (
          <div className="text-center py-12 text-white/60">Cargando miembros...</div>
        ) : authors.length === 0 ? (
          <Card className="card-viubux-dark border-purple-500/30">
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 mx-auto text-white/20 mb-4" />
              <p className="text-white/60">
                {searchTerm ? 'No se encontraron miembros con esa búsqueda' : 'No hay miembros disponibles aún'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {authors.map((author) => (
              <Card 
                key={author.id}
                className="card-viubux-dark border-purple-500/20 hover:border-purple-500/50 transition-colors cursor-pointer"
                onClick={() => openAuthorProfile(author.id)}
                data-testid={`member-card-${author.id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {author.profile_photo ? (
                        <img 
                          src={author.profile_photo} 
                          alt={author.full_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-white text-xl gotham-bold">
                          {author.full_name?.charAt(0)?.toUpperCase()}
                        </span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="gotham-bold text-lg text-white truncate">
                          {author.full_name}
                        </h3>
                        {/* Indicador Online */}
                        {author.is_online && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-500/20 border border-green-500/40">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            <span className="text-green-400 text-[10px] gotham-medium">EN LÍNEA</span>
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          author.user_type === 'author' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {author.user_type === 'author' ? 'Autor' : 'Audio-Lector'}
                        </span>
                      </div>
                      {author.profession && (
                        <p className="text-purple-400 text-sm flex items-center gap-1">
                          <Briefcase className="w-3 h-3" />
                          {author.profession}
                        </p>
                      )}
                      
                      {/* Badges */}
                      {author.badges?.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {author.badges.slice(0, 3).map((badge, i) => (
                            <span 
                              key={i}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-400"
                            >
                              <Award className="w-3 h-3" />
                              {badge}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bio preview */}
                  {author.bio && (
                    <p className="text-white/60 text-sm mt-4 line-clamp-2 verdana-regular">
                      {author.bio}
                    </p>
                  )}

                  {/* Quick info */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                    <div className="flex gap-2">
                      {author.social_instagram && <Instagram className="w-4 h-4 text-pink-400" />}
                      {author.social_facebook && <Facebook className="w-4 h-4 text-blue-400" />}
                      {author.social_linkedin && <Linkedin className="w-4 h-4 text-blue-500" />}
                      {author.social_youtube && <Youtube className="w-4 h-4 text-red-500" />}
                    </div>
                    <span className="text-purple-400 text-sm flex items-center gap-1">
                      Ver perfil <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Author Profile Modal */}
        {selectedAuthor && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="p-6 border-b border-white/10">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {selectedAuthor.profile_photo ? (
                      <img 
                        src={selectedAuthor.profile_photo} 
                        alt={selectedAuthor.full_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white text-3xl gotham-bold">
                        {selectedAuthor.full_name?.charAt(0)?.toUpperCase()}
                      </span>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h2 className="gotham-bold text-2xl text-white">
                        {selectedAuthor.full_name}
                      </h2>
                      {/* Indicador Online en perfil */}
                      {selectedAuthor.is_online && (
                        <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/20 border border-green-500/50">
                          <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></span>
                          <span className="text-green-400 text-xs gotham-bold">EN LÍNEA</span>
                        </span>
                      )}
                    </div>
                    {selectedAuthor.username && (
                      <p className="text-white/40">@{selectedAuthor.username}</p>
                    )}
                    {selectedAuthor.profession && (
                      <p className="text-purple-400 flex items-center gap-2 mt-1">
                        <Briefcase className="w-4 h-4" />
                        {selectedAuthor.profession}
                      </p>
                    )}
                    
                    {/* Badges */}
                    {selectedAuthor.badges?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {selectedAuthor.badges.map((badge, i) => (
                          <span 
                            key={i}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                          >
                            <Award className="w-3 h-3" />
                            {badge}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setSelectedAuthor(null)}
                    className="text-white/60 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Bio */}
                {selectedAuthor.bio && (
                  <div>
                    <h3 className="gotham-bold text-white mb-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-purple-400" />
                      Sobre mí
                    </h3>
                    <p className="text-white/70 verdana-regular whitespace-pre-wrap">
                      {selectedAuthor.bio}
                    </p>
                  </div>
                )}

                {/* Mission */}
                {selectedAuthor.mission && (
                  <div>
                    <h3 className="gotham-bold text-white mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4 text-green-400" />
                      Mi Misión
                    </h3>
                    <p className="text-white/70 verdana-regular">
                      {selectedAuthor.mission}
                    </p>
                  </div>
                )}

                {/* Added Value */}
                {selectedAuthor.added_value && (
                  <div>
                    <h3 className="gotham-bold text-white mb-2 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-400" />
                      Valor Agregado
                    </h3>
                    <p className="text-white/70 verdana-regular">
                      {selectedAuthor.added_value}
                    </p>
                  </div>
                )}

                {/* Discount Offer */}
                {selectedAuthor.discount_offer && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <h3 className="gotham-bold text-green-400 mb-2 flex items-center gap-2">
                      <Percent className="w-4 h-4" />
                      Oferta Especial
                    </h3>
                    <p className="text-white/70 verdana-regular">
                      {selectedAuthor.discount_offer}
                    </p>
                  </div>
                )}

                {/* Contact & Social */}
                <div className="space-y-4">
                  <h3 className="gotham-bold text-white flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-purple-400" />
                    Contacto
                  </h3>

                  <div className="grid grid-cols-2 gap-3">
                    {selectedAuthor.whatsapp && (
                      <a
                        href={`https://wa.me/${selectedAuthor.whatsapp.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-lg hover:bg-green-500/30 transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        WhatsApp
                      </a>
                    )}
                    {selectedAuthor.social_facebook && (
                      <a
                        href={selectedAuthor.social_facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-500/30 transition-colors"
                      >
                        <Facebook className="w-4 h-4" />
                        Facebook
                      </a>
                    )}
                    {selectedAuthor.social_instagram && (
                      <a
                        href={selectedAuthor.social_instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-pink-500/20 text-pink-400 px-4 py-2 rounded-lg hover:bg-pink-500/30 transition-colors"
                      >
                        <Instagram className="w-4 h-4" />
                        Instagram
                      </a>
                    )}
                    {selectedAuthor.social_linkedin && (
                      <a
                        href={selectedAuthor.social_linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-blue-600/20 text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-600/30 transition-colors"
                      >
                        <Linkedin className="w-4 h-4" />
                        LinkedIn
                      </a>
                    )}
                    {selectedAuthor.social_twitter && (
                      <a
                        href={selectedAuthor.social_twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-sky-500/20 text-sky-400 px-4 py-2 rounded-lg hover:bg-sky-500/30 transition-colors"
                      >
                        <Twitter className="w-4 h-4" />
                        Twitter/X
                      </a>
                    )}
                    {selectedAuthor.social_youtube && (
                      <a
                        href={selectedAuthor.social_youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-red-500/20 text-red-400 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-colors"
                      >
                        <Youtube className="w-4 h-4" />
                        YouTube
                      </a>
                    )}
                  </div>

                  {!selectedAuthor.whatsapp && !selectedAuthor.social_facebook && 
                   !selectedAuthor.social_instagram && !selectedAuthor.social_linkedin && 
                   !selectedAuthor.social_twitter && !selectedAuthor.social_youtube && (
                    <p className="text-white/40 text-sm text-center py-4">
                      Este autor no ha compartido información de contacto
                    </p>
                  )}
                </div>
              </div>

              {/* Works / Obras Section */}
              {selectedAuthor.works && selectedAuthor.works.length > 0 && (
                <div className="space-y-4">
                  <h3 className="gotham-bold text-white flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-purple-400" />
                    Obras Publicadas
                  </h3>
                  <div className="grid gap-4">
                    {selectedAuthor.works.map((work, idx) => (
                      <a 
                        key={idx}
                        href={work.reader_url || `https://app.viubux.com/search?q=${encodeURIComponent(work.title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex gap-4 bg-gradient-to-r from-purple-500/10 to-transparent border border-purple-500/30 rounded-xl p-3 hover:border-purple-400/60 hover:from-purple-500/20 transition-all cursor-pointer"
                      >
                        {/* Book Cover */}
                        <div className="flex-shrink-0 w-20 h-28 rounded-lg overflow-hidden bg-purple-900/30 shadow-lg group-hover:shadow-purple-500/20 transition-shadow">
                          {work.cover_image || work.cover_url ? (
                            <img 
                              src={work.cover_image || work.cover_url} 
                              alt={work.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-800/50 to-purple-900/50">
                              <BookOpen className="w-8 h-8 text-purple-400/50" />
                            </div>
                          )}
                        </div>
                        
                        {/* Book Info */}
                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                          <h4 className="gotham-bold text-white text-sm leading-tight group-hover:text-purple-300 transition-colors line-clamp-2">
                            {work.title}
                          </h4>
                          {work.isbn && (
                            <p className="text-white/40 text-xs mt-1">ISBN: {work.isbn}</p>
                          )}
                          {work.description && (
                            <p className="text-white/50 text-xs mt-2 line-clamp-2">{work.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-purple-400 text-xs flex items-center gap-1 group-hover:text-purple-300">
                              <Headphones className="w-3 h-3" />
                              {work.file_type === 'audio' ? 'Escuchar' : 'Ver'} en Viubux
                            </span>
                            <ExternalLink className="w-3 h-3 text-purple-400/50 group-hover:text-purple-300" />
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="p-6 border-t border-white/10 flex gap-3">
                {/* Don't show message button to yourself */}
                {selectedAuthor.id !== user?.id && (
                  <Button
                    onClick={() => {
                      setMessageRecipient(selectedAuthor);
                      setShowMessages(true);
                      setSelectedAuthor(null);
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Mensaje
                  </Button>
                )}
                <Button
                  onClick={() => setSelectedAuthor(null)}
                  variant="outline"
                  className={`border-purple-500/50 text-white hover:bg-purple-500/20 ${selectedAuthor.id === user?.id ? 'w-full' : 'flex-1'}`}
                >
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Messages Panel */}
        <MessagesPanel 
          token={token} 
          isOpen={showMessages} 
          onClose={() => {
            setShowMessages(false);
            setMessageRecipient(null);
          }}
          initialRecipient={messageRecipient}
        />

        {/* Disclaimer Modal - Full Legal Version */}
        {showDisclaimer && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <Shield className="w-8 h-8 text-purple-400" />
                  <div>
                    <h2 className="gotham-bold text-xl text-white">Limitación de Responsabilidad en Interacciones entre Usuarios</h2>
                    <p className="text-white/60 text-sm">Cláusula de Deslinde de Responsabilidad: Zona de Networking</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
                <div className="space-y-4 text-white/70 verdana-regular text-sm leading-relaxed">
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <p className="text-white font-semibold mb-2">1. Naturaleza del Servicio</p>
                    <p>El Usuario reconoce y acepta que ViubuxConnect®, a través de su Zona de Networking, actúa exclusivamente como un <span className="text-purple-400">facilitador de contacto</span> y un puente de comunicación entre los miembros de su comunidad. El objetivo de esta sección es permitir el descubrimiento de perfiles profesionales y propuestas de valor, sin que esto constituya una recomendación, aval o certificación de dichos servicios por parte de la plataforma.</p>
                  </div>
                  
                  <div className="bg-black/30 rounded-lg p-4">
                    <p className="text-white font-semibold mb-2">2. Exclusión de Responsabilidad</p>
                    <p className="mb-3">Ni Viubux, ni sus empresas filiales, subsidiarias, franquiciatarios, accionistas, representantes legales, directivos, empleados o dependientes (en adelante, "Las Entidades Viubux"), asumen responsabilidad alguna, directa o indirecta, por:</p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>La veracidad, calidad, legalidad o puntualidad de los servicios, productos o información ofrecidos por los usuarios.</li>
                      <li>Los acuerdos, contratos, negociaciones o promesas realizados entre miembros de la comunidad.</li>
                      <li>Cualquier intercambio de valor, transacción financiera o pago que ocurra derivado del contacto inicial en la plataforma.</li>
                      <li>Cualquier conflicto, daño, perjuicio o incumplimiento que surja de las relaciones establecidas entre usuarios, ya sea que dichas interacciones ocurran dentro del portal Connect.Viubux.com o en cualquier ámbito externo (digital o físico).</li>
                    </ul>
                  </div>
                  
                  <div className="bg-black/30 rounded-lg p-4">
                    <p className="text-white font-semibold mb-2">3. Independencia de las Partes</p>
                    <p>El Usuario entiende que cada miembro de la comunidad es un ente independiente y que no existe relación laboral, de agencia, sociedad o asociación en participación entre Las Entidades Viubux y los usuarios que ofrecen beneficios o servicios en la red de networking.</p>
                  </div>
                  
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <p className="text-yellow-400 font-semibold mb-2">4. Aceptación de Riesgo</p>
                    <p>Al utilizar la Zona de Networking y contactar a otros miembros, el Usuario asume <span className="text-yellow-400">plena responsabilidad y riesgo</span> por dichas interacciones. El Usuario libera a Las Entidades Viubux de cualquier reclamo, demanda o acción legal presente o futura relacionada con las negociaciones o servicios derivados de dicho contacto.</p>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-white/10">
                <div className="flex items-start gap-3 mb-6">
                  <div 
                    onClick={() => setAcceptedTerms(!acceptedTerms)}
                    className={`w-6 h-6 flex-shrink-0 border-2 rounded cursor-pointer flex items-center justify-center transition-colors ${
                      acceptedTerms 
                        ? 'bg-purple-500 border-purple-500' 
                        : 'border-white/30 hover:border-purple-500/50'
                    }`}
                    data-testid="accept-terms-checkbox"
                  >
                    {acceptedTerms && (
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <label 
                    onClick={() => setAcceptedTerms(!acceptedTerms)}
                    className="text-white/80 verdana-regular text-sm cursor-pointer select-none"
                  >
                    He leído y acepto el Deslinde de Responsabilidad de la Zona de Networking
                  </label>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowDisclaimer(false);
                      setAcceptedTerms(false);
                    }}
                    variant="outline"
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleEnableNetworking}
                    disabled={!acceptedTerms || savingVisibility}
                    className="flex-1 btn-viubux disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="enable-networking-btn"
                  >
                    {savingVisibility ? 'Guardando...' : 'Activar mi perfil'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security Protocol Modal */}
        {showSecurityProtocol && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-blue-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className="w-8 h-8 text-blue-400" />
                    <div>
                      <h2 className="gotham-bold text-xl text-white">Protocolo de Seguridad</h2>
                      <p className="text-white/60 text-sm">Conecta con Inteligencia</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowSecurityProtocol(false)}
                    className="text-white/60 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <p className="text-white/70 verdana-regular text-sm">
                  Para que tu experiencia en nuestra comunidad sea productiva y segura, te sugerimos seguir estos pasos al contactar a otros profesionales:
                </p>
                
                <div className="space-y-4">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white gotham-bold text-sm">1</span>
                      </div>
                      <div>
                        <p className="text-blue-400 gotham-bold mb-1">Verifica el Perfil y la Propuesta de Valor</p>
                        <p className="text-white/70 verdana-regular text-sm">
                          Antes de iniciar un contacto, revisa detenidamente el perfil del miembro. Los usuarios con perfiles completos, enlaces a portafolios verificados y testimonios dentro de la red suelen ser la opción más confiable.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white gotham-bold text-sm">2</span>
                      </div>
                      <div>
                        <p className="text-purple-400 gotham-bold mb-1">Define el Alcance por Escrito</p>
                        <p className="text-white/70 verdana-regular text-sm mb-2">
                          Aunque el contacto inicial sea por chat o llamada, siempre formaliza el acuerdo por correo electrónico o un contrato simple. Asegúrate de que ambos tengan claro:
                        </p>
                        <ul className="text-white/60 text-sm space-y-1 ml-4">
                          <li>• ¿Qué servicio se entregará exactamente?</li>
                          <li>• ¿Cuál es el tiempo de entrega?</li>
                          <li>• ¿Cuál es el costo y qué incluye?</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white gotham-bold text-sm">3</span>
                      </div>
                      <div>
                        <p className="text-green-400 gotham-bold mb-1">Pagos Inteligentes (Hitos)</p>
                        <p className="text-white/70 verdana-regular text-sm">
                          Para servicios de alto valor, te recomendamos <span className="text-green-400">no realizar el pago total por adelantado</span>. Acuerda un esquema de hitos o entregables: Anticipo para iniciar el proyecto y Liquidación contra entrega del trabajo final.
                        </p>
                        <p className="text-white/50 text-xs mt-2 italic">
                          Nota: ViubuxConnect no gestiona estos pagos; utiliza plataformas seguras que ofrezcan protección al consumidor si es necesario.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white gotham-bold text-sm">4</span>
                      </div>
                      <div>
                        <p className="text-orange-400 gotham-bold mb-1">Reporta Comportamientos Sospechosos</p>
                        <p className="text-white/70 verdana-regular text-sm">
                          Si detectas un perfil falso, alguien que ofrece servicios fraudulentos o recibes spam agresivo, utiliza el botón <span className="text-orange-400">"Reportar Incidente"</span>. Nuestra comunidad se mantiene brillante gracias a la vigilancia de todos.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white gotham-bold text-sm">5</span>
                      </div>
                      <div>
                        <p className="text-yellow-400 gotham-bold mb-1">Respeto al "Beneficio de Comunidad"</p>
                        <p className="text-white/70 verdana-regular text-sm">
                          Si contactas a alguien por un descuento exclusivo de ViubuxConnect, menciónalo desde el primer mensaje. Sin embargo, recuerda que el profesional tiene derecho a validar tu membresía activa antes de aplicar cualquier beneficio.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t border-white/10">
                <Button
                  onClick={() => setShowSecurityProtocol(false)}
                  className="w-full btn-viubux"
                >
                  Entendido
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Report Incident Modal */}
        {showReportForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-orange-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-8 h-8 text-orange-400" />
                    <div>
                      <h2 className="gotham-bold text-xl text-white">Formulario de Reporte de Incidente</h2>
                      <p className="text-white/60 text-sm">ViubuxConnect® - Confidencial</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowReportForm(false)}
                    className="text-white/60 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-5">
                <p className="text-white/60 verdana-regular text-sm">
                  Este reporte es confidencial y será revisado por nuestro equipo de Cumplimiento en un plazo de 48 a 72 horas.
                </p>
                
                {/* Reported User Info */}
                <div className="space-y-4">
                  <h3 className="text-white gotham-bold text-sm border-b border-white/10 pb-2">Información del Usuario Reportado</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Nombre/Perfil en Networking *</label>
                      <input
                        type="text"
                        value={reportData.reported_user_name}
                        onChange={(e) => setReportData({...reportData, reported_user_name: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500"
                        placeholder="Nombre del usuario reportado"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Servicio u Oferta que promocionaba</label>
                      <input
                        type="text"
                        value={reportData.reported_service}
                        onChange={(e) => setReportData({...reportData, reported_service: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500"
                        placeholder="Ej: Diseño gráfico, Asesoría legal..."
                      />
                    </div>
                  </div>
                </div>
                
                {/* Incident Type */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">Naturaleza del Incidente *</label>
                  <select
                    value={reportData.incident_type}
                    onChange={(e) => setReportData({...reportData, incident_type: e.target.value})}
                    className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500"
                  >
                    <option value="">Selecciona una opción</option>
                    <option value="incumplimiento">Incumplimiento de Acuerdo (No entregó el servicio/producto pactado)</option>
                    <option value="fraude">Fraude o Estafa (Solicitó pago y desapareció)</option>
                    <option value="comportamiento">Comportamiento Inapropiado (Acoso, lenguaje ofensivo)</option>
                    <option value="spam">Spam Masivo (Publicidad no deseada)</option>
                    <option value="marca">Uso Indebido de Marca (Se hizo pasar por representante de Viubux)</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                
                {/* Incident Details */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Fecha del contacto inicial</label>
                    <input
                      type="date"
                      value={reportData.incident_date}
                      onChange={(e) => setReportData({...reportData, incident_date: e.target.value})}
                      className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div 
                        onClick={() => setReportData({...reportData, had_payment: !reportData.had_payment})}
                        className={`w-6 h-6 border-2 rounded cursor-pointer flex items-center justify-center transition-colors ${
                          reportData.had_payment 
                            ? 'bg-orange-500 border-orange-500' 
                            : 'border-white/30 hover:border-orange-500/50'
                        }`}
                      >
                        {reportData.had_payment && (
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="text-white/70 text-sm">¿Hubo un intercambio de valor (pago)?</span>
                    </label>
                  </div>
                </div>
                
                {reportData.had_payment && (
                  <div className="grid md:grid-cols-2 gap-4 bg-orange-500/5 border border-orange-500/20 rounded-lg p-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Monto del pago</label>
                      <input
                        type="text"
                        value={reportData.payment_amount}
                        onChange={(e) => setReportData({...reportData, payment_amount: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500"
                        placeholder="Ej: $5,000 MXN"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Método de pago utilizado</label>
                      <input
                        type="text"
                        value={reportData.payment_method}
                        onChange={(e) => setReportData({...reportData, payment_method: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500"
                        placeholder="Ej: Transferencia bancaria, PayPal..."
                      />
                    </div>
                  </div>
                )}
                
                {/* Description */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">Descripción breve de lo sucedido *</label>
                  <textarea
                    value={reportData.description}
                    onChange={(e) => setReportData({...reportData, description: e.target.value})}
                    className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500 min-h-[120px]"
                    placeholder="Sé específico con fechas, promesas hechas y el punto exacto del conflicto..."
                  />
                </div>
                
                {/* Desired Resolution */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">¿Qué acción esperas por parte de ViubuxConnect?</label>
                  <input
                    type="text"
                    value={reportData.desired_resolution}
                    onChange={(e) => setReportData({...reportData, desired_resolution: e.target.value})}
                    className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500"
                    placeholder="Ej: Bloqueo del usuario, mediación, simple notificación de alerta..."
                  />
                </div>
                
                {/* Legal Notice */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-yellow-400/80 verdana-regular text-xs">
                    <strong>Nota Legal:</strong> Al enviar este reporte, declaras bajo protesta de decir verdad que la información proporcionada es fidedigna. Te recordamos que, conforme a nuestros T&C, ViubuxConnect actúa únicamente como facilitador de contacto y no se hace responsable de las pérdidas económicas derivadas de tratos entre particulares, pero utilizaremos esta información para proteger la integridad de la comunidad.
                  </p>
                </div>
              </div>
              
              <div className="p-6 border-t border-white/10 flex gap-3">
                <Button
                  onClick={() => setShowReportForm(false)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmitReport}
                  disabled={submittingReport}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {submittingReport ? 'Enviando...' : 'Enviar Reporte'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkingPage;
